import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { sanitizeHtml } from "@/lib/sanitize";
import { toast } from "sonner";
import { fmtAmount } from "@/lib/currency";
import PaymentMethodSelector from "@/components/portal/PaymentMethodSelector";

// ─── Helpers ────────────────────────────────────────────────────────────────

const ccyFmt = (v, ccy = "USD") => {
  try { return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy }).format(Number(v || 0)); }
  catch { return fmtAmount(v, ccy); }
};

const STEPS = [
  { key: "estimate",   label: "Review Estimate",  icon: "FileCheck2" },
  { key: "engagement", label: "Sign Agreement",    icon: "FileSignature" },
  { key: "payment",    label: "Initial Payment",   icon: "CreditCard" },
];

// ─── Step Indicator ─────────────────────────────────────────────────────────

function StepIndicator({ steps, current }) {
  return (
    <div className="flex items-center gap-0" data-testid="onboarding-step-indicator">
      {steps.map((s, i) => {
        const Ic = Icons[s.icon] || Icons.Circle;
        const isDone = steps.indexOf(steps.find(x => x.key === current)) > i;
        const isCurrent = s.key === current;
        return (
          <div key={s.key} className="flex items-center flex-1 last:flex-none" data-testid={`onboarding-step-${s.key}`}>
            <div className="flex flex-col items-center min-w-0">
              <div className={`h-9 w-9 rounded-full flex items-center justify-center border-2 transition-all ${
                isDone ? "bg-[#F5A623] border-[#F5A623] text-[#171B2E]" :
                isCurrent ? "bg-[#171B2E] border-[#F5A623] text-[#F5A623]" :
                "bg-white border-slate-200 text-slate-400"}`}>
                {isDone ? <Icons.Check className="h-4 w-4" /> : <Ic className="h-4 w-4" />}
              </div>
              <span className={`text-xs mt-1.5 font-medium hidden sm:block ${isCurrent ? "text-[#171B2E]" : isDone ? "text-[#F5A623]" : "text-slate-400"}`}>
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 mt-[-18px] sm:mt-[-24px] transition-all ${isDone ? "bg-[#F5A623]" : "bg-slate-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Estimate Step ───────────────────────────────────────────────────────────

function EstimateStep({ estimateRef, onComplete }) {
  const [est, setEst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    portalApi.get(`/portal/estimates/${estimateRef.id}`)
      .then(r => setEst(r.data))
      .catch(() => setEst(estimateRef))
      .finally(() => setLoading(false));
  }, [estimateRef.id]);

  const accept = async () => {
    if (!agree) return toast.error("Please confirm you agree before accepting.");
    setBusy(true);
    try {
      await portalApi.post(`/portal/estimates/${estimateRef.id}/accept`, { agree: true });
      toast.success("Estimate accepted — preparing your agreement letter.");
      onComplete();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "already_accepted") {
        toast.info("Estimate already accepted.");
        onComplete();
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not accept estimate. Please try again.");
      }
    } finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623]" /></div>;
  if (!est) return <p className="text-slate-500 text-center py-10">Estimate unavailable.</p>;

  const ccy = est.currency || "USD";
  const items = (est.line_items || []).slice(0, 6);

  return (
    <div data-testid="onboarding-estimate-step">
      <div className="mb-5">
        <h2 className="font-heading text-xl font-bold text-[#171B2E]" data-testid="onboarding-estimate-title">
          {est.title || "Service Estimate"}
        </h2>
        {est.estimate_number && <p className="text-xs text-slate-500 mt-0.5">{est.estimate_number}</p>}
        {est.scope_summary && <p className="text-sm text-slate-600 mt-2">{est.scope_summary}</p>}
      </div>

      {items.length > 0 && (
        <div className="border border-slate-100 rounded-xl overflow-hidden mb-4" data-testid="onboarding-estimate-items">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-left text-xs text-slate-500 font-medium">
              <th className="px-4 py-2.5">Service</th>
              <th className="px-4 py-2.5 text-right">Amount</th>
            </tr></thead>
            <tbody>
              {items.map((li, i) => (
                <tr key={li.id || i} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 text-slate-700">{li.name || li.description}</td>
                  <td className="px-4 py-2.5 text-right font-medium text-slate-800">
                    {ccyFmt(li.amount ?? (li.unit_price * (li.quantity ?? 1)), ccy)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="bg-slate-50 px-4 py-3 flex justify-between text-sm font-bold text-slate-900 border-t border-slate-200">
            <span>Total</span>
            <span data-testid="onboarding-estimate-total">{ccyFmt(est.total, ccy)}</span>
          </div>
        </div>
      )}

      {!items.length && est.total != null && (
        <div className="bg-slate-50 rounded-xl p-4 mb-4 flex justify-between font-bold text-slate-900">
          <span>Total</span>
          <span data-testid="onboarding-estimate-total">{ccyFmt(est.total, ccy)}</span>
        </div>
      )}

      {est.payment_terms && (
        <p className="text-xs text-slate-500 mb-4"><span className="font-medium">Payment terms:</span> {est.payment_terms}</p>
      )}

      <label className="flex items-start gap-3 cursor-pointer mb-5 p-3 rounded-lg border border-slate-200 hover:border-[#F5A623]/50 transition-colors" data-testid="onboarding-estimate-agree-label">
        <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#F5A623]" data-testid="onboarding-estimate-agree" />
        <span className="text-sm text-slate-700">
          I agree to the estimated services and charges as described above.
        </span>
      </label>

      <div className="flex flex-col sm:flex-row gap-3">
        <button onClick={accept} disabled={busy || !agree}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-50 text-[#171B2E] font-semibold rounded-lg py-2.5 text-sm"
          data-testid="onboarding-estimate-accept">
          {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Check className="h-4 w-4" />}
          Accept Estimate
        </button>
      </div>
    </div>
  );
}

// ─── Engagement Step ─────────────────────────────────────────────────────────

function EngagementStep({ engagementRef, onComplete }) {
  const { profile } = usePortalAuth();
  const [eng, setEng] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agree, setAgree] = useState(false);
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSignature(profile?.full_name || "");
    portalApi.get(`/portal/engagements/${engagementRef.id}`)
      .then(r => setEng(r.data))
      .catch(() => setEng({ ...engagementRef, error: true }))
      .finally(() => setLoading(false));
  }, [engagementRef.id, profile]);

  const sign = async () => {
    if (!agree) return toast.error("Please confirm you agree to the terms.");
    if (!signature.trim()) return toast.error("Please type your full name to sign.");
    setBusy(true);
    try {
      await portalApi.post(`/portal/engagements/${engagementRef.id}/sign`, { agree: true, typed_signature: signature });
      toast.success("Agreement signed! Preparing next step…");
      onComplete();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not sign. Please try again.");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623]" /></div>;
  if (!eng || eng.error) return (
    <div className="text-center py-10">
      <Icons.AlertTriangle className="h-8 w-8 text-[#F5A623] mx-auto mb-2" />
      <p className="text-slate-500 text-sm">Agreement letter unavailable. Please refresh or contact your advisor.</p>
    </div>
  );

  if (eng.signed) {
    return (
      <div className="text-center py-10" data-testid="onboarding-engagement-already-signed">
        <Icons.CheckCircle className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
        <p className="font-semibold text-slate-800">Agreement already signed</p>
        <p className="text-sm text-slate-500 mt-1">Signed on {(eng.signed_at || "").slice(0, 10)}</p>
        <button onClick={onComplete} className="mt-4 text-sm text-[#F5A623] font-medium hover:underline" data-testid="onboarding-engagement-continue">
          Continue →
        </button>
      </div>
    );
  }

  return (
    <div data-testid="onboarding-engagement-step">
      <div className="mb-4">
        <h2 className="font-heading text-xl font-bold text-[#171B2E]">
          {eng.title || "Engagement Letter"}
        </h2>
        {eng.engagement_number && <p className="text-xs text-slate-500 mt-0.5">{eng.engagement_number}</p>}
      </div>

      {eng.letter_body && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-5 max-h-64 overflow-y-auto text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none"
          data-testid="onboarding-engagement-letter"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(eng.letter_body) }} />
      )}

      {/* Pricing snapshot if available */}
      {eng.pricing?.total != null && (
        <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 mb-4 flex justify-between text-sm">
          <span className="text-slate-500">Engagement Total</span>
          <span className="font-bold text-slate-900">{ccyFmt(eng.pricing.total, eng.pricing.currency || "USD")}</span>
        </div>
      )}

      <label className="flex items-start gap-3 cursor-pointer mb-4 p-3 rounded-lg border border-slate-200 hover:border-[#F5A623]/50 transition-colors" data-testid="onboarding-engagement-agree-label">
        <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#F5A623]" data-testid="onboarding-engagement-agree" />
        <span className="text-sm text-slate-700">
          I have read and agree to the terms of this engagement letter.
        </span>
      </label>

      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Type your full legal name to sign
        </label>
        <input type="text" value={signature} onChange={e => setSignature(e.target.value)}
          placeholder="Your full name"
          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 focus:border-[#F5A623]"
          data-testid="onboarding-engagement-signature" />
      </div>

      <button onClick={sign} disabled={busy || !agree || !signature.trim()}
        className="w-full inline-flex items-center justify-center gap-2 bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-50 text-[#171B2E] font-semibold rounded-lg py-2.5 text-sm"
        data-testid="onboarding-engagement-sign">
        {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.PenLine className="h-4 w-4" />}
        Sign Agreement
      </button>
    </div>
  );
}

// ─── Payment Step ────────────────────────────────────────────────────────────

function PaymentStep({ invoice, onExitDashboard }) {
  const ccy = invoice.currency || "USD";
  const balance = invoice.balance ?? invoice.balance_due ?? invoice.total;

  const PAYMENT_STATUS = {
    awaiting_payment: "Awaiting Payment",
    issued: "Awaiting Payment",
    sent: "Awaiting Payment",
    viewed: "Awaiting Payment",
    partially_paid: "Partially Paid",
    overdue: "Overdue — Payment Required",
    payment_failed: "Previous Payment Failed",
    payment_submitted: "Payment Verification Pending",
  };

  return (
    <div data-testid="onboarding-payment-step">
      <h2 className="font-heading text-xl font-bold text-[#171B2E] mb-1">Initial Payment</h2>
      <p className="text-sm text-slate-500 mb-5">
        Your engagement requires a payment before work can begin.
      </p>

      <div className="bg-[#171B2E] text-white rounded-2xl p-5 mb-5" data-testid="onboarding-payment-summary">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide">Invoice</div>
            <div className="font-mono text-sm font-semibold mt-0.5">{invoice.invoice_number}</div>
          </div>
          <span className="text-xs font-semibold bg-amber-400/20 text-amber-300 rounded-full px-2.5 py-1">
            {PAYMENT_STATUS[invoice.status] || invoice.status}
          </span>
        </div>
        <div className="border-t border-white/10 pt-3">
          <div className="text-xs text-slate-400">Amount Due</div>
          <div className="text-3xl font-bold mt-0.5" data-testid="onboarding-payment-balance">
            {ccyFmt(balance, ccy)}
          </div>
          {invoice.status === "partially_paid" && invoice.amount_paid > 0 && (
            <div className="text-xs text-slate-400 mt-1">
              {ccyFmt(invoice.amount_paid, ccy)} already paid · {ccyFmt(invoice.total, ccy)} total
            </div>
          )}
        </div>
      </div>

      <PaymentMethodSelector
        invoiceId={invoice.id}
        returnPath="/portal/onboarding"
        onExitDashboard={onExitDashboard}
      />
    </div>
  );
}

// ─── Preparing State ─────────────────────────────────────────────────────────

function PreparingState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="onboarding-preparing">
      <div className="h-14 w-14 rounded-full bg-[#F5A623]/15 flex items-center justify-center mb-4">
        <Icons.Loader2 className="h-7 w-7 text-[#F5A623] animate-spin" />
      </div>
      <p className="font-semibold text-slate-800">{message || "Preparing your next step…"}</p>
      <p className="text-sm text-slate-500 mt-1">This takes just a moment. Please wait.</p>
    </div>
  );
}

// ─── Success Screen ───────────────────────────────────────────────────────────

function SuccessScreen({ onDashboard }) {
  useEffect(() => {
    const t = setTimeout(onDashboard, 6000);
    return () => clearTimeout(t);
  }, [onDashboard]);

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="onboarding-success">
      <div className="h-20 w-20 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
        <Icons.CheckCircle className="h-10 w-10 text-emerald-500" />
      </div>
      <h2 className="font-heading text-2xl font-bold text-[#171B2E] mb-2">You're all set!</h2>
      <p className="text-slate-500 text-sm max-w-sm">
        Your engagement is confirmed and your team has been notified. We'll be in touch shortly.
      </p>
      <button onClick={onDashboard}
        className="mt-6 inline-flex items-center gap-2 bg-[#F5A623] hover:bg-[#e0951a] text-[#171B2E] font-semibold rounded-lg px-6 py-2.5 text-sm"
        data-testid="onboarding-go-dashboard">
        <Icons.ArrowRight className="h-4 w-4" />Go to my dashboard
      </button>
      <p className="text-xs text-slate-400 mt-3">Redirecting automatically in a few seconds…</p>
    </div>
  );
}

// ─── Main Wizard ─────────────────────────────────────────────────────────────

export default function PortalOnboarding() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollTimer, setPollTimer] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [preparingMsg, setPreparingMsg] = useState("");
  // Track whether this wizard session ever had an active step.
  // When true + status becomes inactive → show success screen.
  // When false + status is inactive on initial load → redirect to dashboard.
  const [wasActive, setWasActive] = useState(false);

  // Clear any prior dismiss so re-entering the wizard resets the session guard
  useEffect(() => { sessionStorage.removeItem("tf_onboarding_dismissed"); }, []);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await portalApi.get("/portal/onboarding-status");
      setStatus(data);
      return data;
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll while preparing (waiting for background automation to produce next step)
  const startPolling = useCallback((msg) => {
    setPreparing(true);
    setPreparingMsg(msg);
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      const data = await loadStatus();
      if (!data) return;
      // Stop polling when step changes or onboarding completes
      const noLongerPreparing = !data.active || data.current_step !== null;
      if (noLongerPreparing || attempts >= 20) {
        clearInterval(interval);
        setPollTimer(null);
        setPreparing(false);
      }
    }, 3000);
    setPollTimer(interval);
    return interval;
  }, [loadStatus]);

  useEffect(() => {
    loadStatus();
    return () => { if (pollTimer) clearInterval(pollTimer); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track active state transitions to distinguish "just completed" from "already established"
  useEffect(() => {
    if (!status) return;
    if (status.active) {
      // Wizard had live steps in this session — mark so success screen shows on completion
      setWasActive(true);
    } else if (!wasActive) {
      // Inactive on first load with no prior active step → established / no engagement
      navigate("/portal", { replace: true });
    }
    // If !active && wasActive → success screen (handled by isComplete below)
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStepComplete = useCallback(async (nextMsg) => {
    setPreparing(true);
    setPreparingMsg(nextMsg || "Preparing your next step…");
    // Immediate re-poll, then keep polling until step advances
    const data = await loadStatus();
    if (data && data.active && data.current_step) {
      setPreparing(false);
    } else if (data && !data.active) {
      setPreparing(false);
    } else {
      // Step not yet ready — start background polling
      startPolling(nextMsg || "Preparing your next step…");
    }
  }, [loadStatus, startPolling]);

  const toDashboard = useCallback(() => {
    // Mark dismissed for this browser session so PortalDashboard won't immediately
    // bounce them back to the wizard. Flag lives in sessionStorage so it clears on
    // tab/window close — meaning a fresh login will still land at the wizard.
    sessionStorage.setItem("tf_onboarding_dismissed", "1");
    navigate("/portal", { replace: true });
  }, [navigate]);

  // Compute active steps for indicator (only show steps present in this client's cycle)
  const activeSteps = STEPS.filter(s => {
    if (!status) return true;
    if (s.key === "estimate" && !status.estimate && status.current_step !== "estimate") return false;
    if (s.key === "payment" && !status.engagement?.payment_required && !status.invoice) return false;
    return true;
  });

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <Icons.Loader2 className="h-8 w-8 animate-spin text-[#F5A623]" />
    </div>
  );

  // Show success when onboarding completes — only when wizard had active steps this session
  const isComplete = status && !status.active && wasActive;
  if (isComplete) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <SuccessScreen onDashboard={toDashboard} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="portal-onboarding">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#171B2E]">Complete Your Onboarding</h1>
          <p className="text-slate-500 text-sm mt-0.5">Follow the steps below to get started with your engagement.</p>
        </div>
        <button onClick={toDashboard}
          className="text-slate-400 hover:text-slate-600 text-sm flex items-center gap-1.5 transition-colors"
          data-testid="onboarding-exit">
          <Icons.X className="h-4 w-4" />Exit
        </button>
      </div>

      {/* Step Indicator — always show when steps are defined */}
      {activeSteps.length >= 1 && status?.current_step && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-6 py-5 mb-5">
          <StepIndicator steps={activeSteps} current={status.current_step} />
        </div>
      )}

      {/* Step Content */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-6 py-6">
        {preparing ? (
          <PreparingState message={preparingMsg} />
        ) : !status?.current_step ? (
          <PreparingState message="Getting your onboarding ready…" />
        ) : status.current_step === "estimate" && status.estimate ? (
          <EstimateStep
            estimateRef={status.estimate}
            onComplete={() => handleStepComplete("Preparing your agreement letter…")}
          />
        ) : status.current_step === "engagement" && status.engagement ? (
          <EngagementStep
            engagementRef={status.engagement}
            onComplete={() => handleStepComplete("Preparing payment details…")}
          />
        ) : status.current_step === "payment" && status.invoice ? (
          <PaymentStep
            invoice={status.invoice}
            onExitDashboard={toDashboard}
          />
        ) : (
          <PreparingState message="Almost there — finishing up…" />
        )}
      </div>

      <p className="text-center text-xs text-slate-400 mt-4">
        Need help? <a href="/portal/messages" className="text-[#F5A623] hover:underline">Message your advisor</a>
      </p>
    </div>
  );
}
